/**
 * Recording Activity (feat-342) — Bespoke-admin-only report: is every phone
 * actually recording? Latest shift per person over the last 7 days, grouped by
 * location, from `GET /api/admin/recording-activity` (CONTRACT.md r1).
 *
 * The backend computes every age, every count and the status ladder; this page
 * renders `status` + `status_reason` verbatim and never derives health. Missing
 * values render `—` — never 0 and never "healthy" (feat-274).
 *
 * Design: worktrees/feat-342-report-mic-activity/design/01-recording-activity.html
 * + NOTES.md. Chips reuse the feat-274 device-health palette (deviceHealth.ts);
 * the table uses the frozen-first-column recipe (components.md, feat-241), so
 * Person is the first (frozen) column and Status second.
 *
 * RBAC: the route is wrapped in <AdminRoute> (App.tsx); the endpoint is
 * `require_admin`. This component does not re-gate.
 *
 * Privacy: counts and timestamps only — no transcript text, quotes or audio links.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import ScorecardNavigation from '../ScorecardNavigation';
import { HEALTH_CHIP_BASE, formatSeconds } from '../deviceHealth';
import {
  DEFAULT_SORT,
  MISSING,
  RecordingActivityResponse,
  RecordingActivityRow,
  RecordingStatus,
  SortKey,
  SortState,
  TenantOption,
  formatBattery,
  formatCount,
  formatLast,
  formatLocalTime,
  formatSpan,
  formatThreshold,
  quietSinceLocal,
  getRecordingActivity,
  groupByLocation,
  httpStatusOf,
  listTenantsForPicker,
  nextSort,
  pickDefaultTenant,
  sortRows,
  summarize,
  todayLocalDate,
} from '../../../services/recordingActivityService';

export const REFRESH_INTERVAL_MS = 60_000;

/** On-screen copy (constants, not literals — grep these). */
export const RECORDING_ACTIVITY_COPY = {
  back: 'Bespoke Admin',
  title: 'Recording activity',
  staffOnly: '🔒 Bespoke staff only',
  subtitle: 'Latest shift per person, started in the last 7 days. Ages are measured by the server.',
  tenantLabel: 'Tenant',
  refresh: 'Refresh',
  loading: 'Loading recording activity…',
  emptyTitle: (tenant: string) => `No one at ${tenant} has started a shift in the last 7 days.`,
  emptyDetail: 'A person appears here once their phone opens a shift. Older shifts are not shown.',
  noTenants: 'No tenants available.',
  tenantsFailed: 'Couldn’t load the tenant list.',
  refreshFailed: 'Couldn’t refresh. Showing the last data that loaded.',
  loadFailed: 'Couldn’t load recording activity.',
  retryDetail: (status: number | null) =>
    `${status ? `Request failed (${status}).` : 'Request failed.'} It will try again automatically in 60s.`,
  tryAgain: 'Try again',
  updated: (ago: string) => `Updated ${ago} ago`,
  open: 'Open',
  ended: 'Ended',
  /** r2 Shift status cell for never_ended rows: "Not ended" + "quiet since {time}". */
  notEnded: 'Not ended',
  quietSince: (time: string) => `quiet since ${time}`,
  // The backend's end_reason is the raw feat-274 EndReason value (user_ended,
  // admin_ended, grace_expired, audio_stalled, duration_cap, transcript_gap).
  // Show it verbatim: a "by …" prefix reads wrong for most of them
  // ("by audio_stalled"), and we never invent wording for it.
  endedBy: (reason: string) => reason,
  noData: 'No data',
  key: {
    red: (audio: string, hb: string) =>
      audio === hb
        ? `open shift, no audio or no heartbeat for ${audio}`
        : `open shift, no audio for ${audio} or no heartbeat for ${hb}`,
    amber: (tx: string) => `built-in mic, or no transcript for ${tx}`,
    green: 'recording normally',
    neverEnded: (after: string) => `open shift, nothing received for over ${after}`,
    ended: 'shift closed',
  },
} as const;

// ---- Status chip: whole literals beside HEALTH_CHIP_BASE, never interpolated ----

export const STATUS_LABEL: Record<RecordingStatus, string> = {
  red: 'Red',
  amber: 'Amber',
  green: 'Green',
  never_ended: 'Never ended',
  ended: 'Ended',
};

/**
 * Fill + ink — same mapping as deviceHealth HEALTH_CHIP (stalled / no_telemetry /
 * recording / unknown / offline). never_ended reuses the muted `unknown` outline
 * chip: muted like Ended but a visibly different shape (white + ring vs filled).
 */
const STATUS_CHIP: Record<RecordingStatus, string> = {
  red: 'bg-danger text-white',
  amber: 'bg-warning-surface text-attention-on',
  green: 'bg-success-light text-success-on',
  never_ended: 'bg-white text-inkMuted ring-1 ring-inkMuted/40',
  ended: 'bg-cream-dark text-inkMuted',
};

/** Leading dot. Red leads with an AlertTriangle instead; never_ended is a hollow dot. */
const STATUS_DOT: Record<RecordingStatus, string> = {
  red: '',
  amber: 'w-2 h-2 rounded-full bg-warning-solid',
  green: 'w-2 h-2 rounded-full bg-success',
  never_ended: 'w-2 h-2 rounded-full border border-inkMuted',
  ended: 'w-2 h-2 rounded-full bg-inkMuted',
};

const KEY_DOT: Record<RecordingStatus, string> = {
  red: 'w-2 h-2 rounded-full bg-danger',
  amber: 'w-2 h-2 rounded-full bg-warning-solid',
  green: 'w-2 h-2 rounded-full bg-success',
  never_ended: 'w-2 h-2 rounded-full border border-inkMuted',
  ended: 'w-2 h-2 rounded-full bg-inkMuted',
};

const REASON_CLASS: Record<RecordingStatus, string> = {
  red: 'block mt-1 max-w-[200px] text-[11px] font-bold leading-snug text-danger-text',
  amber: 'block mt-1 max-w-[200px] text-[11px] font-bold leading-snug text-attention-on',
  green: 'block mt-1 max-w-[200px] text-[11px] font-bold leading-snug text-inkMuted',
  never_ended: 'block mt-1 max-w-[200px] text-[11px] font-bold leading-snug text-inkMuted',
  ended: 'block mt-1 max-w-[200px] text-[11px] font-bold leading-snug text-inkMuted',
};

export const StatusChip: React.FC<{ status: RecordingStatus; reason: string | null }> = ({ status, reason }) => (
  <>
    <span
      data-testid="status-chip"
      data-status={status}
      title={reason ?? undefined}
      className={`${HEALTH_CHIP_BASE} ${STATUS_CHIP[status]}`}
    >
      {status === 'red' ? (
        <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
      ) : (
        <span className={STATUS_DOT[status]} aria-hidden="true" />
      )}
      {STATUS_LABEL[status]}
    </span>
    {reason && (
      <span data-testid="status-reason" className={REASON_CLASS[status]}>
        {reason}
      </span>
    )}
  </>
);

// ---- Columns ----

interface Column {
  key: SortKey;
  label: string;
  /** The spec's full name, shown as the header tooltip. */
  title?: string;
  minW: string;
  right?: boolean;
}

/** Person first (frozen), then Status — design/NOTES.md "Table". */
const COLUMNS: Column[] = [
  { key: 'server_name', label: 'Person', minW: 'min-w-[176px]' },
  { key: 'status', label: 'Status', minW: 'min-w-[176px]' },
  { key: 'shift_started_local', label: 'Shift started', minW: 'min-w-[104px]' },
  { key: 'shift_status', label: 'Shift status', minW: 'min-w-[136px]' },
  { key: 'shift_length_seconds', label: 'Length', title: 'Shift length', minW: 'min-w-[80px]', right: true },
  { key: 'audio_seconds', label: 'Audio', title: 'Audio received', minW: 'min-w-[80px]', right: true },
  { key: 'transcript_part_count', label: 'Parts', title: 'Transcript parts', minW: 'min-w-[88px]', right: true },
  // Open rows show time since; ended / never-ended rows show the gap before the end (r2).
  { key: 'last_audio_age_seconds', label: 'Last audio', title: 'Since last audio chunk (closed shifts: gap before the end)', minW: 'min-w-[112px]', right: true },
  { key: 'last_transcript_age_seconds', label: 'Last transcript', title: 'Since last transcript part (closed shifts: gap before the end)', minW: 'min-w-[120px]', right: true },
  { key: 'last_heartbeat_age_seconds', label: 'Last heartbeat', title: 'Since last device_telemetry heartbeat (closed shifts: gap before the end)', minW: 'min-w-[120px]', right: true },
  { key: 'mic_route', label: 'Mic route', minW: 'min-w-[120px]' },
  { key: 'battery_level', label: 'Battery', minW: 'min-w-[72px]', right: true },
  { key: 'ws_reconnect_count', label: 'Reconnects', title: 'WebSocket reconnects', minW: 'min-w-[96px]', right: true },
  { key: 'device', label: 'Device', minW: 'min-w-[160px]' },
];

const Missing: React.FC = () => (
  <span className="text-inkMuted" aria-label={RECORDING_ACTIVITY_COPY.noData}>
    {MISSING}
  </span>
);

/** Render a formatted value, swapping the `—` sentinel for the styled missing span. */
const val = (s: string) => (s === MISSING ? <Missing /> : s);

const TD = 'px-3 py-2.5 border-t border-softBorder align-top';
const TD_NOWRAP = `${TD} whitespace-nowrap`;
const TD_NUM = `${TD} text-right tabular-nums whitespace-nowrap`;

const Row: React.FC<{ r: RecordingActivityRow; today: string }> = ({ r, today }) => {
  const deviceRest = [r.app_version, r.patch_number !== null ? `p${r.patch_number}` : null].filter(Boolean) as string[];
  return (
    <tr data-testid="activity-row" data-server-id={r.server_id} className="group hover:bg-cream">
      <th
        scope="row"
        className="sticky left-0 z-20 bg-white group-hover:bg-cream px-3 py-2.5 border-t border-softBorder shadow-[1px_0_0_0_rgba(0,0,0,0.08)] font-semibold text-ink align-top text-left"
      >
        <span className="block max-w-[152px] truncate" title={r.server_name}>
          {r.server_name || <Missing />}
        </span>
      </th>
      <td className={TD}>
        <StatusChip status={r.status} reason={r.status_reason} />
      </td>
      <td className={`${TD_NOWRAP} tabular-nums`}>{val(formatLocalTime(r.shift_started_local, today))}</td>
      <td className={TD_NOWRAP}>
        {r.status === 'never_ended' ? (
          <>
            {RECORDING_ACTIVITY_COPY.notEnded}
            <span data-testid="quiet-since" className="block text-[11px] text-inkMuted tabular-nums">
              {RECORDING_ACTIVITY_COPY.quietSince(formatLocalTime(quietSinceLocal(r), today))}
            </span>
          </>
        ) : r.is_open ? (
          RECORDING_ACTIVITY_COPY.open
        ) : (
          `${RECORDING_ACTIVITY_COPY.ended} ${formatLocalTime(r.shift_ended_local, today)}`
        )}
        {r.end_reason && (
          <span data-testid="end-reason" title="Shift end reason" className="block font-mono text-[11px] text-inkMuted">{RECORDING_ACTIVITY_COPY.endedBy(r.end_reason)}</span>
        )}
      </td>
      <td className={TD_NUM}>{val(formatSpan(r.shift_length_seconds))}</td>
      <td className={TD_NUM}>{val(formatSpan(r.audio_seconds))}</td>
      <td className={TD_NUM}>{val(formatCount(r.transcript_part_count))}</td>
      <td className={TD_NUM} data-testid="last-audio">{val(formatLast(r, 'audio'))}</td>
      <td className={TD_NUM} data-testid="last-transcript">{val(formatLast(r, 'transcript'))}</td>
      <td className={TD_NUM} data-testid="last-heartbeat">{val(formatLast(r, 'heartbeat'))}</td>
      <td className={`${TD_NOWRAP} font-mono text-xs`}>{r.mic_route ?? <Missing />}</td>
      <td className={TD_NUM}>{val(formatBattery(r.battery_level))}</td>
      <td className={TD_NUM}>{val(formatCount(r.ws_reconnect_count))}</td>
      <td className={TD_NOWRAP}>
        {!r.device_model && !deviceRest.length ? (
          <Missing />
        ) : (
          <>
            {r.device_model}
            {deviceRest.length > 0 && (
              <span className="text-inkMuted">
                {r.device_model ? ' · ' : ''}
                {deviceRest.join(' · ')}
              </span>
            )}
          </>
        )}
      </td>
    </tr>
  );
};

const SortHeader: React.FC<{ c: Column; sort: SortState; onSort: (k: SortKey) => void }> = ({ c, sort, onSort }) => {
  const active = sort.key === c.key;
  const frozen = c.key === 'server_name';
  const thCls = frozen
    ? `sticky left-0 z-30 w-[176px] ${c.minW} bg-cream-dark px-3 py-2.5 shadow-[1px_0_0_0_rgba(0,0,0,0.08)] text-left`
    : `${c.minW} bg-cream-dark px-3 py-2.5 ${c.right ? 'text-right' : 'text-left'}`;
  const btnCls = active
    ? 'inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide whitespace-nowrap text-ink'
    : 'inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide whitespace-nowrap text-inkMuted hover:text-ink';
  return (
    <th scope="col" aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'} className={thCls}>
      <button type="button" data-testid={`sort-${c.key}`} title={c.title} onClick={() => onSort(c.key)} className={btnCls}>
        {c.label}
        {active && (
          <span aria-hidden="true" className="text-[10px]">
            {sort.dir === 'desc' ? '▼' : '▲'}
          </span>
        )}
      </button>
    </th>
  );
};

function useNowTick(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

const RecordingActivityReport: React.FC = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantsError, setTenantsError] = useState(false);
  const [tenantsLoaded, setTenantsLoaded] = useState(false);

  const [data, setData] = useState<RecordingActivityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  const reqSeq = useRef(0);
  const now = useNowTick();

  // Tenant list → default to Ladle & Leaf (fallback: first tenant).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listTenantsForPicker();
        if (cancelled) return;
        setTenants([...list].sort((a, b) => a.tenant_name.localeCompare(b.tenant_name)));
        setTenantId(pickDefaultTenant(list)?.tenant_id ?? null);
      } catch {
        if (!cancelled) setTenantsError(true);
      } finally {
        if (!cancelled) setTenantsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async (tid: string, opts: { reset?: boolean } = {}) => {
    const seq = ++reqSeq.current;
    if (opts.reset) {
      // Switching tenants: never leave the old tenant's rows under the new name.
      setData(null);
      setError(null);
      setUpdatedAt(null);
    }
    setLoading(true);
    try {
      const resp = await getRecordingActivity(tid);
      if (seq !== reqSeq.current) return; // superseded (e.g. tenant switch)
      setData(resp);
      setError(null);
      setUpdatedAt(Date.now());
    } catch (e) {
      if (seq !== reqSeq.current) return;
      // Keep the previous data on screen; surface the failure inline.
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, []);

  // Load on tenant change, and auto-refresh every 60 s for that tenant.
  useEffect(() => {
    if (!tenantId) return undefined;
    load(tenantId, { reset: true });
    const id = setInterval(() => load(tenantId), REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [tenantId, load]);

  const refresh = () => {
    if (tenantId) load(tenantId);
  };

  const groups = useMemo(() => groupByLocation(data?.rows ?? []), [data]);
  const today = todayLocalDate(new Date(now));
  const tenantName = tenants.find((t) => t.tenant_id === tenantId)?.tenant_name ?? '';
  const th = data?.thresholds;
  const C = RECORDING_ACTIVITY_COPY;

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <div className="max-w-[1600px] mx-auto p-4 md:p-8">
        <button
          type="button"
          onClick={() => navigate('/restaurant/internal')}
          className="inline-flex items-center gap-1.5 text-sm text-inkMuted hover:text-ink mb-4"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          {C.back}
        </button>

        <div className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-3xl font-display font-semibold text-ink">{C.title}</h1>
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-coral bg-coral-light/50 border border-coral/40 rounded-full px-2 py-0.5">
                {C.staffOnly}
              </span>
            </div>
            <p className="text-sm text-inkMuted">{C.subtitle}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm text-ink flex items-center gap-2">
              <span className="text-inkMuted">{C.tenantLabel}</span>
              <select
                data-testid="tenant-picker"
                className="border border-softBorder rounded-lg px-3 py-2 text-sm bg-white font-semibold text-ink focus:outline-none focus:ring-2 focus:ring-coral/40"
                value={tenantId ?? ''}
                onChange={(e) => setTenantId(e.target.value || null)}
                disabled={!tenants.length}
              >
                {tenants.map((t) => (
                  <option key={t.tenant_id} value={t.tenant_id}>
                    {t.tenant_name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              data-testid="refresh-button"
              onClick={refresh}
              disabled={!tenantId || loading}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-softBorder bg-white text-ink hover:bg-cream-dark disabled:opacity-60"
            >
              <RefreshCw className={loading ? 'w-4 h-4 animate-spin' : 'w-4 h-4'} aria-hidden="true" />
              {C.refresh}
            </button>
            {updatedAt !== null && (
              <span
                data-testid="updated-stamp"
                className="text-xs text-inkMuted tabular-nums whitespace-nowrap"
                title={`Last successful load ${new Date(updatedAt).toLocaleTimeString()}`}
              >
                {C.updated(formatSeconds((now - updatedAt) / 1000))}
              </span>
            )}
          </div>
        </div>

        {tenantsError && (
          <div role="alert" className="mb-5 rounded-2xl border border-danger-line/40 bg-danger-surface p-3 text-sm text-danger-text flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-danger" aria-hidden="true" />
            <p className="font-bold">{C.tenantsFailed}</p>
          </div>
        )}

        {error && (
          <div
            role="alert"
            data-testid="refresh-error"
            className="mb-5 rounded-2xl border border-danger-line/40 bg-danger-surface p-3 text-sm text-danger-text flex items-start gap-3"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-danger" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <p className="font-bold">{data ? C.refreshFailed : C.loadFailed}</p>
              <p className="text-xs mt-0.5">{C.retryDetail(httpStatusOf(error))}</p>
            </div>
            <button
              type="button"
              data-testid="try-again"
              onClick={refresh}
              disabled={loading}
              className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-lg border border-danger-line/40 bg-white text-danger-text hover:bg-danger-em disabled:opacity-60"
            >
              {C.tryAgain}
            </button>
          </div>
        )}

        {tenantsLoaded && !tenants.length && !tenantsError && <p className="text-sm text-inkMuted">{C.noTenants}</p>}

        {!data && loading && (
          <div data-testid="first-load">
            <div className="flex items-center gap-2 text-sm text-inkMuted mb-4">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              {C.loading}
            </div>
            <section className="bg-white rounded-3xl border border-softBorder shadow-sm p-5 mb-5" aria-busy="true">
              <div className="h-5 w-32 rounded bg-cream-dark animate-pulse mb-2" />
              <div className="h-4 w-64 rounded bg-cream-dark animate-pulse mb-4" />
              <div className="space-y-2">
                <div className="h-9 rounded-lg bg-cream animate-pulse" />
                <div className="h-9 rounded-lg bg-cream animate-pulse" />
                <div className="h-9 rounded-lg bg-cream animate-pulse" />
              </div>
            </section>
          </div>
        )}

        {th && (
          <p data-testid="threshold-key" className="mb-4 text-xs text-inkMuted flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1.5">
              <span className={KEY_DOT.red} aria-hidden="true" />
              <b className="text-ink">{STATUS_LABEL.red}</b>{' '}
              {C.key.red(formatThreshold(th.red_no_audio_seconds), formatThreshold(th.red_no_heartbeat_seconds))}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className={KEY_DOT.amber} aria-hidden="true" />
              <b className="text-ink">{STATUS_LABEL.amber}</b> {C.key.amber(formatThreshold(th.amber_no_transcript_seconds))}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className={KEY_DOT.green} aria-hidden="true" />
              <b className="text-ink">{STATUS_LABEL.green}</b> {C.key.green}
            </span>
            {typeof th.never_ended_after_seconds === 'number' && (
              <span data-testid="key-never-ended" className="inline-flex items-center gap-1.5">
                <span className={KEY_DOT.never_ended} aria-hidden="true" />
                <b className="text-ink">{STATUS_LABEL.never_ended}</b>{' '}
                {C.key.neverEnded(formatThreshold(th.never_ended_after_seconds))}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <span className={KEY_DOT.ended} aria-hidden="true" />
              <b className="text-ink">{STATUS_LABEL.ended}</b> {C.key.ended}
            </span>
          </p>
        )}

        {data && groups.length === 0 && (
          <div data-testid="empty-state" className="rounded-2xl border border-softBorder bg-white p-4 text-sm">
            <p className="text-ink font-semibold">{C.emptyTitle(tenantName)}</p>
            <p className="text-xs text-inkMuted mt-1">{C.emptyDetail}</p>
          </div>
        )}

        {groups.map((g) => {
          const s = summarize(g.rows);
          const headingId = `loc-${g.location_id || g.location_name}`;
          return (
            <section
              key={g.location_id || g.location_name}
              data-testid="location-section"
              data-location={g.location_name}
              aria-labelledby={headingId}
              className="bg-white rounded-3xl border border-softBorder shadow-sm p-5 mb-5"
            >
              <div className="mb-3">
                <h2 id={headingId} className="font-display font-semibold text-lg leading-tight text-ink">
                  {g.location_name}
                </h2>
                <p data-testid="section-summary" className="text-sm text-inkMuted tabular-nums">
                  {s.people} {s.people === 1 ? 'person' : 'people'} · {s.recording} recording ·{' '}
                  {s.red ? <b className="text-danger-text">{s.red} red</b> : `${s.red} red`} ·{' '}
                  {s.amber ? <b className="text-attention-on">{s.amber} amber</b> : `${s.amber} amber`} ·{' '}
                  {/* Muted at 0; ink (not an alarm colour) when non-zero. */}
                  {s.never_ended ? (
                    <b className="text-ink">{s.never_ended} never ended</b>
                  ) : (
                    `${s.never_ended} never ended`
                  )}
                </p>
              </div>
              <div className="min-w-0">
                <div className="relative w-fit max-w-full overflow-x-auto rounded-2xl border border-softBorder">
                  <table className="w-max border-separate border-spacing-0 text-left">
                    <thead>
                      <tr>
                        {COLUMNS.map((c) => (
                          <SortHeader key={c.key} c={c} sort={sort} onSort={(k) => setSort((cur) => nextSort(cur, k))} />
                        ))}
                      </tr>
                    </thead>
                    <tbody className="text-sm text-ink">
                      {sortRows(g.rows, sort).map((r) => (
                        <Row key={r.shift_id || r.server_id} r={r} today={today} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default RecordingActivityReport;
